import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { ChevronDown, FileText, Handshake, Package, UserRound } from "lucide-react";
import Navbar from "../Components/Header/Navbar";
import Footer from "../Components/Footer/Footer";
import TermsOfUse, { DocumentId } from "../Components/Legal/TermsOfUse";
import PrivacyPolicy from "../Components/Legal/PrivacyPolicy";

export type LegalTabId = "terms" | "privacy";

type LegalTabConfig = {
  id: LegalTabId;
  label: string;
  content: React.ReactNode;
};

type TermsDocumentConfig = {
  id: DocumentId;
  label: string;
  icon: React.ReactNode;
};

const getLegalStateFromHash = (hashValue: string) => {
  const hash = hashValue.replace("#", "");

  if (hash === "privacy") {
    return {
      activeTab: "privacy" as LegalTabId,
      activeTermsDocument: "terms_conditions" as DocumentId,
    };
  }

  if (hash === "terms-user" || hash === "user_terms") {
    return {
      activeTab: "terms" as LegalTabId,
      activeTermsDocument: "user_terms" as DocumentId,
    };
  }

  if (hash === "terms-product" || hash === "product_terms") {
    return {
      activeTab: "terms" as LegalTabId,
      activeTermsDocument: "product_terms" as DocumentId,
    };
  }

  if (
    hash === "terms-operational" ||
    hash === "operational_leasing_agreement"
  ) {
    return {
      activeTab: "terms" as LegalTabId,
      activeTermsDocument: "operational_leasing_agreement" as DocumentId,
    };
  }

  return {
    activeTab: "terms" as LegalTabId,
    activeTermsDocument: "terms_conditions" as DocumentId,
  };
};

export const Legal = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const initialLegalState =
    typeof window !== "undefined"
      ? getLegalStateFromHash(window.location.hash)
      : {
          activeTab: "terms" as LegalTabId,
          activeTermsDocument: "terms_conditions" as DocumentId,
        };

  const [activeTab, setActiveTab] = useState<LegalTabId>(
    initialLegalState.activeTab,
  );
  const [activeTermsDocument, setActiveTermsDocument] = useState<DocumentId>(
    initialLegalState.activeTermsDocument,
  );
  const [termsExpanded, setTermsExpanded] = useState(true);
  const [mobileNavExpanded, setMobileNavExpanded] = useState(true);

  useEffect(() => {
    const { activeTab: nextTab, activeTermsDocument: nextDocument } =
      getLegalStateFromHash(location.hash);

    setActiveTab(nextTab);
    setActiveTermsDocument(nextDocument);
    setTermsExpanded(true);
    setMobileNavExpanded(true);
  }, [location.hash]);

  useEffect(() => {
    const hash =
      activeTab === "privacy"
        ? "privacy"
        : activeTermsDocument === "user_terms"
          ? "terms-user"
          : activeTermsDocument === "product_terms"
            ? "terms-product"
            : activeTermsDocument === "operational_leasing_agreement"
              ? "terms-operational"
              : "terms";

    if (window.location.hash.replace("#", "") !== hash) {
      window.history.replaceState(null, "", `#${hash}`);
    }
  }, [activeTab, activeTermsDocument]);

  const tabs: LegalTabConfig[] = [
    {
      id: "terms",
      label: t("legal.tabs.terms"),
      content: <TermsOfUse activeDocument={activeTermsDocument} />,
    },
    {
      id: "privacy",
      label: t("legal.tabs.privacy"),
      content: <PrivacyPolicy />,
    },
  ];

  const activeTabConfig =
    tabs.find((tab) => tab.id === activeTab) ?? tabs[0];

  const termsDocuments: TermsDocumentConfig[] = [
    {
      id: "terms_conditions",
      label: t("legal.documentTabs.termsConditions"),
      icon: <FileText className="h-4 w-4" />,
    },
    {
      id: "user_terms",
      label: t("legal.documentTabs.userTerms"),
      icon: <UserRound className="h-4 w-4" />,
    },
    {
      id: "product_terms",
      label: t("legal.documentTabs.productTerms"),
      icon: <Package className="h-4 w-4" />,
    },
    {
      id: "operational_leasing_agreement",
      label: t("legal.documentTabs.operationalLeasingAgreement"),
      icon: <Handshake className="h-4 w-4" />,
    },
  ];

  const renderMainTabButton = (tab: LegalTabConfig) => {
    const isActive = tab.id === activeTab;

    return (
      <button
        key={tab.id}
        type="button"
        onClick={() => setActiveTab(tab.id)}
        aria-selected={isActive}
        className={`group w-full rounded-2xl border px-4 py-4 text-left transition-all duration-300 ${isActive
            ? "border-blue-300/60 bg-gradient-to-r from-blue-500/15 to-indigo-500/15 shadow-lg shadow-blue-500/10"
            : "border-white/10 bg-white/5 hover:border-white/20 hover:bg-white/10"
          }`}
      >
        <div className="flex items-center justify-between gap-3">
          <div>
            <p
              className={`text-lg font-semibold transition-colors duration-300 ${isActive
                  ? "text-blue-100"
                  : "text-slate-200 group-hover:text-white"
                }`}
            >
              {tab.label}
            </p>
          </div>
        </div>
      </button>
    );
  };

  return (
    <div className="relative min-h-[100dvh] w-full overflow-clip bg-gradient-to-br from-slate-900 via-gray-900 to-slate-800 flex flex-col">
      <div className="absolute inset-0 z-0 pointer-events-none">
        <div className="absolute -top-40 -right-40 h-96 w-96 rounded-full bg-gradient-to-br from-purple-500/20 to-blue-500/20 blur-3xl" />
        <div className="absolute -bottom-40 -left-40 h-96 w-96 rounded-full bg-gradient-to-tr from-indigo-500/20 to-purple-500/20 blur-3xl" />
        <div className="absolute left-1/2 top-1/2 h-[400px] w-[800px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-gradient-to-r from-blue-100/10 to-purple-100/10 blur-3xl" />
      </div>

      <div className="relative z-10 flex flex-1 flex-col">
        <Navbar />

        <main className="flex-1 px-4 pb-20 pt-[150px] md:px-6 lg:px-10">
          <div className="mx-auto max-w-7xl">
            <div className="mb-8 text-center lg:mb-10 lg:text-left">
              <p className="text-sm font-semibold uppercase tracking-[0.35em] text-blue-200/80">
                {t("legal.pageTag")}
              </p>
              <h1 className="mt-4 text-3xl font-bold tracking-tight text-white md:text-5xl lg:text-6xl">
                {t("legal.pageTitle")}
              </h1>
              <p className="mx-auto mt-3 max-w-3xl text-base leading-7 text-slate-300 md:mt-4 md:text-lg lg:mx-0">
                {t("legal.pageSubtitle")}
              </p>
            </div>

            <div className="lg:hidden space-y-6">
              <div className="sticky top-20 z-20 rounded-2xl border border-white/10 bg-slate-800/95 p-4 shadow-2xl shadow-black/30 ring-1 ring-white/10 backdrop-blur-xl">
                <button
                  type="button"
                  onClick={() => setMobileNavExpanded((value) => !value)}
                  aria-expanded={mobileNavExpanded}
                  className="flex w-full items-center justify-between border-b border-white/15 pb-4 text-left"
                >
                  <h2 className="text-2xl font-bold text-white">
                    {t("legal.navigation.title")}
                  </h2>
                  <ChevronDown
                    className={`h-5 w-5 shrink-0 text-slate-300 transition-transform duration-300 ${mobileNavExpanded ? "rotate-180" : ""
                      }`}
                  />
                </button>

                <div
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${mobileNavExpanded
                      ? "max-h-[520px] opacity-100 pt-5"
                      : "max-h-0 opacity-0 pt-0"
                    }`}
                >
                  <div className="space-y-3">
                    <button
                      type="button"
                      onClick={() => {
                        if (activeTab === "terms") {
                          setTermsExpanded((value) => !value);
                          return;
                        }

                        setActiveTab("terms");
                        setTermsExpanded(true);
                        setMobileNavExpanded(true);
                      }}
                      aria-expanded={activeTab === "terms" && termsExpanded}
                      className={`group flex w-full items-center justify-between rounded-2xl border px-4 py-4 text-left transition-all duration-300 ${activeTab === "terms"
                          ? "border-blue-300/60 bg-gradient-to-r from-blue-500/15 to-indigo-500/15 shadow-lg shadow-blue-500/10"
                          : "border-white/10 bg-slate-800/75 hover:border-white/20 hover:bg-slate-800/90"
                        }`}
                    >
                      <p
                        className={`text-lg font-semibold transition-colors duration-300 ${activeTab === "terms"
                            ? "text-blue-100"
                            : "text-slate-200 group-hover:text-white"
                          }`}
                      >
                        {tabs[0].label}
                      </p>
                      <ChevronDown
                        className={`h-5 w-5 shrink-0 transition-transform duration-300 ${activeTab === "terms" && termsExpanded ? "rotate-180" : ""
                          } ${activeTab === "terms" ? "text-blue-100" : "text-slate-400"
                          }`}
                      />
                    </button>

                    <div
                      className={`overflow-hidden rounded-2xl border border-white/10 border-l-4 border-l-blue-300/40 bg-white/5 shadow-inner shadow-black/10 transition-all duration-300 ease-in-out ${activeTab === "terms" && termsExpanded
                          ? "max-h-[420px] opacity-100 translate-y-0 p-3 pl-6 ml-3"
                          : "max-h-0 opacity-0 -translate-y-1 p-0"
                        }`}
                    >
                      <div
                        className={`space-y-2 transition-opacity duration-200 ${activeTab === "terms" && termsExpanded
                            ? "opacity-100"
                            : "opacity-0"
                          }`}
                      >
                        {termsDocuments.map((document) => {
                          const isActive = document.id === activeTermsDocument;

                          return (
                            <button
                              key={document.id}
                              type="button"
                              onClick={() => {
                                setActiveTab("terms");
                                setTermsExpanded(true);
                                setMobileNavExpanded(true);
                                setActiveTermsDocument(document.id);
                              }}
                              className={`flex w-full items-center gap-3 rounded-xl border px-3 py-3 text-left transition-all duration-300 ${isActive
                                  ? "border-blue-300/60 bg-gradient-to-r from-blue-500/15 to-indigo-500/15 shadow-lg shadow-blue-500/10"
                                  : "border-white/10 bg-slate-800/75 hover:border-white/20 hover:bg-slate-800/90"
                                }`}
                            >
                              <div
                                className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border ${isActive
                                    ? "border-blue-200 bg-white text-blue-700"
                                    : "border-slate-700 bg-slate-950/60 text-slate-400"
                                  }`}
                              >
                                {document.icon}
                              </div>
                              <p
                                className={`text-sm font-semibold leading-5 ${isActive ? "text-blue-100" : "text-slate-200"
                                  }`}
                              >
                                {document.label}
                              </p>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => {
                        setActiveTab("privacy");
                        setMobileNavExpanded(true);
                      }}
                      className={`group flex w-full items-center justify-between rounded-2xl border px-4 py-4 text-left transition-all duration-300 ${activeTab === "privacy"
                          ? "border-blue-300/60 bg-gradient-to-r from-blue-500/15 to-indigo-500/15 shadow-lg shadow-blue-500/10"
                          : "border-white/10 bg-slate-800/75 hover:border-white/20 hover:bg-slate-800/90"
                        }`}
                    >
                      <p
                        className={`text-lg font-semibold transition-colors duration-300 ${activeTab === "privacy"
                            ? "text-blue-100"
                            : "text-slate-200 group-hover:text-white"
                          }`}
                      >
                        {tabs[1].label}
                      </p>
                    </button>
                  </div>
                </div>
              </div>

              <section className="rounded-[2rem] border border-white/10 bg-white/95 p-4 shadow-2xl shadow-black/20 backdrop-blur-xl">
                <div className="rounded-[1.5rem] border border-slate-200 bg-white p-4">
                  <div className="mt-4">{activeTabConfig.content}</div>
                </div>
              </section>
            </div>

            <div className="hidden lg:grid items-start gap-6 lg:grid-cols-[340px_minmax(0,1fr)]">
              <aside className="hidden h-fit self-start rounded-[2rem] border border-white/10 bg-white/10 p-4 shadow-2xl shadow-black/20 backdrop-blur-xl md:p-6 lg:block lg:sticky lg:top-24">
                <div className="border-b border-white/10 pb-4">
                  <h2 className="text-2xl font-bold text-white">
                    {t("legal.navigation.title")}
                  </h2>
                </div>

                <div className="mt-5 space-y-3">
                  <button
                    type="button"
                    onClick={() => {
                      if (activeTab === "terms") {
                        setTermsExpanded((value) => !value);
                        return;
                      }

                      setActiveTab("terms");
                      setTermsExpanded(true);
                    }}
                    aria-expanded={activeTab === "terms" && termsExpanded}
                    className={`group w-full rounded-2xl border px-4 py-4 text-left transition-all duration-300 ${activeTab === "terms"
                        ? "border-blue-300/60 bg-gradient-to-r from-blue-500/15 to-indigo-500/15 shadow-lg shadow-blue-500/10"
                        : "border-white/10 bg-white/5 hover:border-white/20 hover:bg-white/10"
                      }`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p
                          className={`text-lg font-semibold transition-colors duration-300 ${activeTab === "terms"
                              ? "text-blue-100"
                              : "text-slate-200 group-hover:text-white"
                            }`}
                        >
                          {tabs[0].label}
                        </p>
                      </div>
                      <ChevronDown
                        className={`h-5 w-5 shrink-0 transition-transform duration-300 ${activeTab === "terms"
                            ? "text-blue-100"
                            : "text-slate-400"
                          } ${termsExpanded && activeTab === "terms" ? "rotate-180" : ""}`}
                      />
                    </div>
                  </button>

                  <div
                    className={`overflow-hidden rounded-2xl border border-white/10 border-l-4 border-l-blue-300/40 bg-white/5 shadow-inner shadow-black/10 transition-all duration-300 ease-in-out ${activeTab === "terms" && termsExpanded
                        ? "max-h-[420px] opacity-100 translate-y-0 p-3 pl-6 ml-3"
                        : "max-h-0 opacity-0 -translate-y-1 p-0"
                      }`}
                  >
                    <div
                      className={`space-y-2 transition-opacity duration-200 ${activeTab === "terms" && termsExpanded
                          ? "opacity-100"
                          : "opacity-0"
                        }`}
                    >
                      {termsDocuments.map((document) => {
                        const isActive = document.id === activeTermsDocument;

                        return (
                          <button
                            key={document.id}
                            type="button"
                            onClick={() =>
                              setActiveTermsDocument(document.id)
                            }
                            className={`flex w-full items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition-all duration-300 md:py-3 ${isActive
                                ? "border-blue-300/60 bg-gradient-to-r from-blue-500/15 to-indigo-500/15 shadow-lg shadow-blue-500/10"
                                : "border-white/10 bg-white/5 hover:border-white/20 hover:bg-white/10"
                              }`}
                          >
                            <div
                              className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border md:h-9 md:w-9 ${isActive
                                  ? "border-blue-200 bg-white text-blue-700"
                                  : "border-slate-700 bg-slate-900/60 text-slate-400"
                                }`}
                            >
                              {document.icon}
                            </div>
                            <p
                              className={`text-sm font-semibold leading-5 ${isActive
                                  ? "text-blue-100"
                                  : "text-slate-200"
                                }`}
                            >
                              {document.label}
                            </p>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {renderMainTabButton(tabs[1])}
                </div>
              </aside>

              <section className="rounded-[2rem] border border-white/10 bg-white/95 p-4 shadow-2xl shadow-black/20 backdrop-blur-xl md:p-6 lg:p-8">
                <div className="rounded-[1.5rem] border border-slate-200 bg-white p-5 md:p-8">
                  <div className="mt-5 md:mt-8">{activeTabConfig.content}</div>
                </div>
              </section>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </div>
  );
};
